import { useState } from 'react'
import styles from './StageFunnel.module.css'

// Source-owned adaptation of BoardUI 0.5.9's licensed stage-bars-card.tsx:
// https://www.boardui.com/components/stage-bars-card. Keep the shared row grid,
// rounded stage tracks and active-tone treatment. The host supplies widths,
// connectors and formatted values; no conversion is inferred across sources.
// `change` and connector.icon are presentation slots, keeping feature/domain
// helpers outside this leaf. Detail density is content-sized and keeps notes.
export function StageFunnel({ rows, label, slots, stackedSlots, density = 'widget' }) {
  const [hoveredKey, setHoveredKey] = useState(null)
  const [focusedKey, setFocusedKey] = useState(null)
  const isStage = key => rows.some(row => row.kind === 'total' && row.key === key)
  const activeKey = isStage(hoveredKey) ? hoveredKey : isStage(focusedKey) ? focusedKey : null
  const detail = density === 'detail'
  let tone = 0
  return (
    <ol
      className={styles.funnel}
      aria-label={label}
      data-density={density}
      style={{ '--wf-funnel-slots': slots, '--wf-funnel-stacked-slots': stackedSlots }}
      onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocusedKey(null) }}
    >
      {rows.map(row => {
        const total = row.kind === 'total'
        const connector = row.connector
        const stageTone = total ? tone++ % 5 : undefined
        return (
          <li
            key={row.key}
            className={styles.funnelStage}
            data-kind={row.kind}
            data-state={connector && connector.state !== 'tracked' ? connector.state : undefined}
            data-tone={stageTone}
            data-with-connector={connector ? '' : undefined}
            data-active={activeKey === row.key || undefined}
            data-muted={total && activeKey != null && activeKey !== row.key || undefined}
            tabIndex={total ? 0 : undefined}
            data-chart-inspection={total ? '' : undefined}
            onFocus={total ? () => { setHoveredKey(null); setFocusedKey(row.key) } : undefined}
            onPointerEnter={total ? event => { if (event.pointerType !== 'touch') setHoveredKey(row.key) } : undefined}
            onPointerLeave={total ? () => setHoveredKey(null) : undefined}
          >
            {connector && (!detail || !total) ? <Connector connector={connector} /> : null}
            {total ? <Stage row={row} /> : null}
            {connector && detail && total ? <Connector connector={connector} /> : null}
          </li>
        )
      })}
    </ol>
  )
}

function Connector({ connector }) {
  const short = Boolean(connector.shortLabel && connector.shortLabel !== connector.label)
  return (
    <span className={styles.funnelConnector} title={connector.title} data-short={short ? '' : undefined}>
      {connector.icon ? <span className={styles.funnelConnectorIcon} aria-hidden="true">{connector.icon}</span> : null}
      <span className={styles.funnelRate}>{connector.value}</span>
      {connector.label ? <span className={styles.funnelRateLabel} data-long="">{connector.label}</span> : null}
      {short ? <span className={`${styles.funnelRateLabel} ${styles.funnelShort}`} aria-hidden="true">{connector.shortLabel}</span> : null}
      {connector.note ? <span className={styles.funnelNote}>{connector.note}</span> : null}
    </span>
  )
}

function Stage({ row }) {
  const shortLabel = Boolean(row.shortLabel && row.shortLabel !== row.label)
  const shortValue = Boolean(row.short && row.short !== row.value)
  return (
    <>
      <span className={styles.funnelLabel} title={row.label} data-short={shortLabel ? '' : undefined}>
        <span data-long="">{row.label}</span>
        {shortLabel ? <span className={styles.funnelShort} aria-hidden="true">{row.shortLabel}</span> : null}
        {row.note ? <span className={styles.stageNote}>{row.note}</span> : null}
      </span>
      <span className={styles.funnelTrack} aria-hidden="true" data-empty={Number.isFinite(row.count) ? undefined : ''}>
        <span className={styles.funnelFill} style={{ width: row.width }} />
      </span>
      <span className={styles.funnelValue} title={row.value} data-short={shortValue ? '' : undefined}>
        <span data-long="">{row.value}</span>
        {shortValue ? <span className={styles.funnelShort} aria-hidden="true">{row.short}</span> : null}
      </span>
      {row.change ? <span className={styles.funnelChange} data-direction={row.changeDirection} title={row.changeTitle}>{row.change}</span> : null}
    </>
  )
}
